/**
 * SectionWrapper - applies SectionLayout constraints as inline CSS
 * @module screen-view-v2/components/screens/FlexibleLayout
 */

import React, { memo, useContext } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { SectionLayout } from '@segmently/schema-models/v2';
import { mapEdgeInsetsToCSS, mapCornerRadiusToCSS, mapVerticalAlignment, mapHorizontalAlignment, mapSectionSelfAlignment } from '../../../utils/styleMapper';
import { Media } from '../../shared/Media';
import { ArticleRenderContext } from '@modules/screen-view-v2/context/ArticleRenderContext';
import { hasOwn } from '@modules/screen-view-v2/utils/articlePresentationCss';
import type { ArticleSectionSpacing } from '@modules/screen-view-v2/utils/articleSpacingCollapse';

export interface SectionWrapperProps {
  layout?: SectionLayout;
  children: ReactNode;
  className?: string;
  locale?: string;
  'data-testid'?: string;
  'data-section-id'?: string;
  /**
   * Template article flow (G2 Task 17 fix round 1, M-1): the token margins FlexibleLayout collapsed
   * against the neighbouring sections. Absent = this wrapper resolves its own tokens (funnels, sticky).
   */
  articleSpacing?: ArticleSectionSpacing;
}

export const SectionWrapper = memo(function SectionWrapper({
  layout,
  children,
  className,
  locale,
  'data-testid': testId,
  'data-section-id': sectionId,
  articleSpacing,
}: SectionWrapperProps): React.ReactElement {
  // Article template (G2, D4): spacing tokens and surface roles; null outside articles.
  const presentation = useContext(ArticleRenderContext)?.presentation ?? null;
  const style: CSSProperties = {};

  if (layout) {
    // Height sizing
    const { heightKind, heightValue } = layout;
    if (heightKind === 'fixed' && heightValue !== undefined) {
      style.height = heightValue;
      style.minHeight = heightValue;
      style.maxHeight = heightValue;
    } else if (heightKind === 'percent' && heightValue !== undefined) {
      style.height = `${heightValue}%`;
      style.minHeight = `${heightValue}%`;
      style.maxHeight = `${heightValue}%`;
    }
    // Clip overflow for constrained-height sections
    if (heightKind === 'fixed' || heightKind === 'percent') {
      style.overflow = 'hidden';
    }
    // heightKind === 'auto': no height set, natural content height

    // Flex sizing (meaningful when screen scrollable=false)
    if (layout.flexGrow !== undefined) {
      style.flexGrow = layout.flexGrow;
    }
    if (layout.flexShrink !== undefined) {
      style.flexShrink = layout.flexShrink;
    }

    // Section-level scroll
    if (layout.scrollable) {
      style.overflowY = 'auto';
    }

    // Padding
    Object.assign(style, mapEdgeInsetsToCSS(layout.padding));

    // Background
    if (layout.background?.kind === 'solid' && layout.background.color) {
      style.backgroundColor = layout.background.color;
    } else if (layout.background?.kind === 'media') {
      // Media background: position context + isolation creates stacking context
      // so z-index: -1 on media layer paints behind in-flow children
      style.position = 'relative';
      style.isolation = 'isolate';
    } else if ((layout as any).backgroundColor) {
      // Backward compat: legacy data may have backgroundColor instead of background
      style.backgroundColor = (layout as any).backgroundColor;
    }

    // Corner radius
    const cornerRadiiStyles = mapCornerRadiusToCSS(layout.cornerRadius);
    if (Object.keys(cornerRadiiStyles).length > 0) {
      Object.assign(style, cornerRadiiStyles);
      // overflow hidden only when radius is set
      style.overflow = layout.scrollable ? 'auto' : 'hidden';
    }

    // Border
    if (layout.border) {
      const { border } = layout;
      if (border.width !== undefined) {
        style.borderStyle = 'solid';
        style.borderWidth = border.width;
        style.borderColor = border.color ?? 'transparent';
      }
      if (border.radius) {
        Object.assign(style, mapCornerRadiusToCSS(border.radius));
      }
    }

    // Always flex-column with centered content by default
    style.display = 'flex';
    style.flexDirection = 'column';
    style.alignItems = 'center';

    // Vertical alignment override
    if (layout.alignment) {
      style.justifyContent = mapVerticalAlignment(layout.alignment);
    }

    // Margin (can be negative for overlapping effects)
    if (layout.margin) {
      const m = layout.margin;
      if (m.top !== undefined) style.marginTop = m.top;
      if (m.bottom !== undefined) style.marginBottom = m.bottom;
      if (m.start !== undefined) style.marginLeft = m.start;
      if (m.end !== undefined) style.marginRight = m.end;
    }

    // Article spacing tokens → margins, UNDER a literal margin side (D4).
    if (articleSpacing) {
      // Collapsed by FlexibleLayout (M-1); literal sides are never in it, so the block above keeps them.
      if (articleSpacing.marginTop !== undefined) style.marginTop = articleSpacing.marginTop;
      if (articleSpacing.marginBottom !== undefined) style.marginBottom = articleSpacing.marginBottom;
    } else if (presentation?.spacing) {
      if (layout.spaceBefore && hasOwn(presentation.spacing, layout.spaceBefore) && layout.margin?.top === undefined) {
        style.marginTop = `var(--ap-space-${layout.spaceBefore})`;
      }
      if (layout.spaceAfter && hasOwn(presentation.spacing, layout.spaceAfter) && layout.margin?.bottom === undefined) {
        style.marginBottom = `var(--ap-space-${layout.spaceAfter})`;
      }
    }

    // Horizontal alignment override
    if (layout.horizontalAlignment) {
      style.alignItems = mapHorizontalAlignment(layout.horizontalAlignment);
    }

    // Width sizing (v2.19.0) — applied ONLY when explicitly set;
    // absent fields keep legacy full-width flow byte-identical
    const { widthKind, widthValue } = layout;
    if (widthKind === 'fixed' && widthValue !== undefined) {
      style.width = widthValue;
    } else if (widthKind === 'percent' && widthValue !== undefined) {
      style.width = `${widthValue}%`;
    }
    if (layout.maxWidth !== undefined) {
      style.maxWidth = layout.maxWidth;
    }
    if (layout.minWidth !== undefined) {
      style.minWidth = layout.minWidth;
    }
    if (layout.sectionHorizontalAlignment) {
      style.alignSelf = mapSectionSelfAlignment(layout.sectionHorizontalAlignment);
    }

    // Z-index (needs positioning context)
    if (layout.zIndex !== undefined && layout.zIndex !== 0) {
      style.zIndex = layout.zIndex;
      style.position = 'relative';
    }
  }

  // Article surface role (D4, PR5): the style block paints [data-surface]; a literal background stays inline and wins.
  const surfaceRole = layout?.surfaceRole;
  const surface = surfaceRole && presentation?.palette
    && (hasOwn(presentation.palette.light, surfaceRole) || hasOwn(presentation.palette.dark, surfaceRole))
    ? surfaceRole : undefined;

  const hasMediaBackground = layout?.background?.kind === 'media' && layout.background.media;

  return (
    <div
      className={className}
      style={style}
      data-testid={testId}
      data-section-id={sectionId}
      data-surface={surface}
    >
      {hasMediaBackground && (
        <div style={{ position: 'absolute', inset: 0, zIndex: -1, overflow: 'hidden' }}>
          <Media
            media={layout!.background!.media}
            locale={locale ?? 'en'}
            fillContainer
            scaleMode="fill"
          />
        </div>
      )}
      {children}
    </div>
  );
});

SectionWrapper.displayName = 'SectionWrapper';
